import React, { useState } from 'react';
import { Drawer, IconButton, Typography, Box, Button, Tooltip, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DeleteIcon from '@mui/icons-material/Delete';
import { useStyles } from '../../assets/styles/configStyle';
import { constants } from '../../utils/constants';
import { getCookie } from '../login/cookies';
import SnackbarNotification from '../snackbar/snackbarNotification';

interface OptionData {
  id?: string;
  value: string;
  output_value: string;
  project_limit?: number;
  user_limit?: number;
  is_deleted?: boolean;
  config_id?: string;
}

interface OptionsDrawerProps {
  open: boolean;
  onClose: () => void;
  chipLabel: string;
  localListFields: string[];
  outputValues: string[];
  path: boolean;
  showAll: boolean;
  onFieldChange: (index: number, value: string) => void;
  onOutputValueChange: (index: number, value: string) => void;
  onRemoveField: (index: number) => void;
  onAddField: () => void;
  onToggleShowMore: () => void;
  onBulkPaste: () => void;
  projectLimits?: string[];
  userLimits?: string[];
  onProjectLimitChange?: (index: number, value: string) => void;
  onUserLimitChange?: (index: number, value: string) => void;
  optionIds?: (string | undefined)[];
  configId?: string;
  onDone?: () => void;
}

const OptionsDrawer: React.FC<OptionsDrawerProps> = ({
  open,
  onClose,
  chipLabel,
  localListFields,
  outputValues,
  path,
  showAll,
  onFieldChange,
  onOutputValueChange,
  onRemoveField,
  onAddField,
  onToggleShowMore,
  onBulkPaste,
  projectLimits,
  userLimits,
  onProjectLimitChange,
  onUserLimitChange,
  optionIds,
  configId,
  onDone,
}) => {
  const classes = useStyles();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleDeleteClick = (index: number) => {
    // If the option has an ID (existing option), show confirmation dialog
    if (optionIds && optionIds[index]) {
      setDeleteIndex(index);
      setDeleteDialogOpen(true);
    } else {
      // If it's a newly added option without ID, delete directly
      onRemoveField(index);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteIndex === null || !configId) return;

    const optionId = optionIds?.[deleteIndex];
    if (!optionId) {
      setDeleteDialogOpen(false);
      setDeleteIndex(null);
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        config_id: configId,
        options: [
          {
            id: optionId,
            is_deleted: true,
          },
        ],
      };

      const response = await fetch(`${constants.API_BASE_URL}project/company/config_options/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getCookie('token')}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        onRemoveField(deleteIndex);
        setSnackbarMessage('Option deleted successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        throw new Error(result.message || 'Failed to delete option');
      }
    } catch (error) {
      console.error('Error deleting option:', error);
      setSnackbarMessage('Error deleting option');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
      setDeleteDialogOpen(false);
      setDeleteIndex(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleDone = async () => {
    if (!configId || (chipLabel !== 'single_select' && chipLabel !== 'multi_select')) {
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);
      const options: OptionData[] = [];

      // Build the options array
      localListFields.forEach((field, index) => {
        const optionId = optionIds?.[index];
        const option: OptionData = {
          value: field,
          output_value: outputValues?.[index] || '',
          project_limit: projectLimits?.[index] ? parseInt(projectLimits[index]) : 0,
          user_limit: userLimits?.[index] ? parseInt(userLimits[index]) : 0,
        };

        // If option has an ID, it's an existing option being updated
        if (optionId) {
          option.id = optionId;
        }
        // Otherwise, it's a new option to be created

        options.push(option);
      });

      const payload = {
        config_id: configId,
        options: options,
      };

      const response = await fetch(`${constants.API_BASE_URL}project/company/config_options/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getCookie('token')}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSnackbarMessage('Options updated successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTimeout(() => {
          if (onDone) onDone();
          onClose();
        }, 1000);
      } else {
        throw new Error(result.message || 'Failed to save options');
      }
    } catch (error) {
      console.error('Error saving options:', error);
      setSnackbarMessage('Error saving options');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: string, index: number) => (
    <div className={classes.fieldContainer} key={index}>
      <div className={classes.fieldContent}>
        {/* Show radio/checkbox icons for all types except single_select */}
        {['radio', 'discard_list'].includes(chipLabel) && (
          <RadioButtonUncheckedIcon fontSize="medium" className={classes.dragIcon} />
        )}
        {['checkbox', 'multi_select', 'adjectives_list'].includes(chipLabel) && (
          <CheckBoxOutlineBlankIcon fontSize="medium" className={classes.dragIcon} />
        )}
        {path ? (
          <textarea
            placeholder={`Option ${index + 1}`}
            value={field}
            onChange={(e) => onFieldChange(index, e.target.value)}
            style={{
              marginLeft: (chipLabel === 'single_select' || chipLabel === 'multi_select') ? '0px' : '10px',
              marginTop: '10px',
              height: '50px',
              padding: '5px',
              width: '227px',
              border: '1px solid',
            }}
          />
        ) : (
          <>
            <TextField
              size="small"
              label={`Option ${index + 1}`}
              margin="normal"
              value={field}
              onChange={(e) => onFieldChange(index, e.target.value)}
              className={classes.textField}
              sx={{ 
                marginLeft: (chipLabel === 'single_select' || chipLabel === 'multi_select') ? '0px' : undefined,
                width: (chipLabel === 'single_select' || chipLabel === 'multi_select') ? '135px' : undefined,
                '& .MuiInputBase-root': { fontFamily: 'Poppins' },
                '& .MuiInputLabel-root': { fontFamily: 'Poppins' }
              }}
            />
            <TextField
              size="small"
              label="Output Value"
              margin="normal"
              value={outputValues?.[index] ?? ''}
              onChange={(e) => onOutputValueChange(index, e.target.value)}
              className={classes.textField}
              sx={{ 
                width: (chipLabel === 'single_select' || chipLabel === 'multi_select') ? '135px' : undefined,
                '& .MuiInputBase-root': { fontFamily: 'Poppins' },
                '& .MuiInputLabel-root': { fontFamily: 'Poppins' }
              }}
            />
            {(chipLabel === 'single_select' || chipLabel === 'multi_select') && (
              <>
                <TextField
                  size="small"
                  label="Project Limit"
                  margin="normal"
                  type="number"
                  value={projectLimits?.[index] ?? ''}
                  onChange={(e) => onProjectLimitChange?.(index, e.target.value)}
                  className={classes.textField}
                  sx={{ 
                    width: '115px',
                    '& .MuiInputBase-root': { fontFamily: 'Poppins' },
                    '& .MuiInputLabel-root': { fontFamily: 'Poppins' }
                  }}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
                <TextField
                  size="small"
                  label="User Limit"
                  margin="normal"
                  type="number"
                  value={userLimits?.[index] ?? ''}
                  onChange={(e) => onUserLimitChange?.(index, e.target.value)}
                  className={classes.textField}
                  sx={{ 
                    width: '115px',
                    '& .MuiInputBase-root': { fontFamily: 'Poppins' },
                    '& .MuiInputLabel-root': { fontFamily: 'Poppins' }
                  }}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </>
            )}
          </>
        )}
        {index !== 0 && (
          <IconButton
            onClick={() => handleDeleteClick(index)}
            size="small"
            sx={{
              color: '#d32f2f',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: (chipLabel === 'single_select' || chipLabel === 'multi_select') ? '650px' : '500px',
          padding: '20px',
        },
      }}
    >
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 600, color: '#2c3e50' }}>
            Enter Options Manually
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box mb={3} display="flex" gap={2} justifyContent="space-between" alignItems="center">
          <Button
            onClick={onBulkPaste}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderColor: 'black',
              color: 'black',
              fontWeight: 500,
              padding: '8px 20px',
              fontSize: '14px',
              '&:hover': {
                borderColor: 'black',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
            startIcon={<AddIcon />}
          >
            Bulk Paste Options
          </Button>
          
          <Button
            variant="contained"
            onClick={onAddField}
            disabled={chipLabel === 'single_select' && localListFields.length >= 20}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              padding: '8px 24px',
              fontSize: '14px',
              borderRadius: '8px',
              backgroundColor: chipLabel === 'single_select' && localListFields.length >= 20 ? '#ccc' : '#1976d2',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                backgroundColor: chipLabel === 'single_select' && localListFields.length >= 20 ? '#ccc' : '#1565c0',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e',
                boxShadow: 'none',
              },
            }}
            startIcon={<AddIcon />}
          >
            Add Option
          </Button>
        </Box>

        <Box sx={{ paddingBottom: '20px' }}>
          <Box>
            <div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap' }}>
              {Array.isArray(localListFields) &&
                (showAll ? localListFields : localListFields.slice(0, 5)).map((field, index) =>
                  renderField(field, index)
                )}
            </div>
          </Box>
        </Box>

        {localListFields.length > 5 && (
          <Button
            onClick={onToggleShowMore}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: '#1976d2',
              fontWeight: 500,
              marginTop: '10px',
              padding: '6px 16px',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            {showAll ? 'Show Less' : 'See More'}
          </Button>
        )}

        <Box mt={4}>
          <Button 
            variant="contained" 
            onClick={handleDone}
            fullWidth
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              height: '40px',
              borderRadius: '8px',
              backgroundColor: '#3D85C6',
              '&:hover': {
                backgroundColor: '#3D85C6',
              },
              '&:disabled': {
                backgroundColor: '#9e9e9e',
              },
            }}
          >
            {isSubmitting ? 'Saving...' : 'DONE'}
          </Button>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: '8px',
            },
          }}
        >
          <DialogTitle sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>
            Delete Option
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px' }}>
              Are you sure you want to delete this option?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: '16px' }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={isSubmitting}
              sx={{
                textTransform: 'none',
                fontFamily: 'Montserrat',
                color: '#666',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={isSubmitting}
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: 'none',
                fontFamily: 'Montserrat',
                backgroundColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                },
                '&:disabled': {
                  backgroundColor: '#9e9e9e',
                },
              }}
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar Notification */}
        <SnackbarNotification
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </Box>
    </Drawer>
  );
};

export default OptionsDrawer;
